/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { create } from 'zustand';
import type { UserRole, AuthMeDto } from '@shared/types';
import { apiClient } from '../services/apiClient';

interface AuthUser {
  oid: string;
  name: string;
  email: string;
  /** DB-managed role from frm_users (null = no access) */
  role: UserRole;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** true while /api/auth/me is being fetched */
  isFetchingMe: boolean;
  setUser: (user: AuthUser) => void;
  clearUser: () => void;
  hasRole: (role: string) => boolean;
  fetchMe: () => Promise<void>;
}

const ROLE_HIERARCHY: Record<string, number> = {
  Pharmacist: 1,
  Manager: 2,
  Admin: 3,
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isFetchingMe: false,

  setUser: (user) => set({ user, isAuthenticated: true, isFetchingMe: user.role === null }),
  clearUser: () => set({ user: null, isAuthenticated: false }),
  hasRole: (role) => {
    const { user } = get();
    if (!user?.role) return false;
    const userLevel = ROLE_HIERARCHY[user.role] ?? 0;
    const requiredLevel = ROLE_HIERARCHY[role] ?? 0;
    return userLevel >= requiredLevel;
  },

  /**
   * Call GET /api/auth/me to get DB-managed role.
   * Auto-registers user with NULL role on first login.
   */
  fetchMe: async () => {
    set({ isFetchingMe: true });
    try {
      const res = await apiClient.get<AuthMeDto>('/auth/me');
      const me = res.data;
      const current = get().user;
      set({
        user: {
          oid: current?.oid || '',
          name: me.displayName,
          email: me.email,
          role: me.role,
        },
        isAuthenticated: true,
        isFetchingMe: false,
      });
    } catch {
      set({ isFetchingMe: false });
    }
  },
}));
